import { useContext, useEffect, useState } from "react";
import { ColumnConfig, DataSourceConfig } from "../definitions";

import axios from "axios";
import { useSelector } from "react-redux";
import { IUser, UserRole } from "src/store/models/user.model";
import { IRootState } from "src/store/reducers";
import { BASE_API_URL } from "../../../constants/EnvConstants";
import * as K from "../../../constants/StorageConstants";
import { ReportDataContext } from "../ReportDetail";
import { formatValues } from "../utils";

async function promiseRetry<T>(fn: () => Promise<T>, retries = 5, err?: any): Promise<T> {
  await new Promise(resolve => setTimeout(resolve, (5 - retries) * 5000));
  return !retries ? Promise.reject(err) : fn().catch(error => promiseRetry(fn, retries - 1, error));
}

const fetchData = async (sql: string, options: Record<string, any>) => {
  const token = localStorage.getItem(K.ACCESS_TOKEN);

  if (!options) {
    options = {};
  }

  let inputs = {};
  try {
    inputs = { ...options };
    Object.keys(options).forEach(k => {
      const val = options[k];
      if (Array.isArray(val)) {
        if (val[0] && typeof val[0] === "object" && "key" in val[0]) {
          inputs[k] = val.map(v => v.value).join(",");
        }
      } else {
        inputs[k] = val;
      }
    });
  } catch (e) {
    console.error("Error processing inputs", e);
  }

  const res = await axios.post(
    `${BASE_API_URL}report/query`,
    { query: sql, inputs },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return res.data;
};

export const useWidgetFetch = (
  widgetTitle: string | null,
  dataSource?: DataSourceConfig,
  options: Record<string, string> = null,
  columns?: ColumnConfig[],
) => {
  const { sql, returnType, returnExpression, returnExpressionFormat } = dataSource || {};

  const [data, setData] = useState([]);
  const [colNames, setColNames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const reportDataContext = useContext(ReportDataContext);
  const { updateWidgetData } = reportDataContext;

  // default to no branch filter
  if (options) {
    options["_branch"] = "0";
  }

  const user = useSelector<IRootState, IUser>(state => state.auth.user);
  if (user.userRole === UserRole.BranchManager) {
    if (options) {
      options["_branch"] = user.branchTypeID.toString();
    }
  }

  const refreshData = async () => {
    setError(false);
    setIsLoading(true);
    try {
      const data = await promiseRetry(() => fetchData(sql, options));

      updateWidgetData(widgetTitle, data.result, columns);

      if (returnType === "scalar" && returnExpression) {
        const scalarValue = returnExpression.split(".").reduce((acc, key) => acc && acc[key], data.result);
        if (returnExpressionFormat) {
          setData(formatValues(scalarValue, returnExpressionFormat));
        } else {
          setData(scalarValue);
        }
      } else if (data && returnType === "options") {
        const options = data.result.map(Object.values);
        setData(options.map(([value, key]) => ({ key, value })));
      } else {
        if (data) {
          setData(data.result.map(Object.values));
          setColNames(Object.keys(data.result[0] || {}));
        }
      }
    } catch (err) {
      // Handle errors
      console.error("Error fetching data:", err);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sql) {
      refreshData();
    }
  }, [dataSource, options]);

  return { data, colNames, isLoading, error };
};
